/**
 * Visual primitives matching the Besty V2 dashboard (assistant / exec-hub design:
 * bestyDashboard/.../pages/exec-hub/assistant-design.css): neutral scale, 16px cards,
 * 10px controls, near-black primary actions, segmented tabs.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export function cx(...parts: (string | false | undefined | null)[]): string {
  return parts.filter(Boolean).join(' ');
}

export const card =
  'bg-white border border-neutral-200/70 rounded-2xl ' +
  'shadow-[0_1px_2px_rgb(24_35_47/0.03),0_4px_16px_-12px_rgb(24_35_47/0.12)]';
export const labelText = 'text-[13px] text-neutral-500';
export const mutedText = 'text-[13px] text-neutral-500';

export const input =
  'h-9 px-3 text-[13px] rounded-[10px] border border-neutral-200 bg-white text-neutral-900 ' +
  'hover:border-neutral-300 focus:outline-none focus:border-neutral-900';

const buttonBase =
  'inline-flex items-center gap-1.5 h-9 px-3.5 text-[13px] font-medium rounded-[10px] transition-colors ' +
  'disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none';

const BUTTON_VARIANTS = {
  primary: 'bg-[#121212] text-white hover:bg-neutral-800',
  secondary: 'border border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-50',
  ghost: 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100',
} as const;

export function Button({
  variant = 'secondary',
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: keyof typeof BUTTON_VARIANTS }) {
  return (
    <button className={cx(buttonBase, BUTTON_VARIANTS[variant], className)} {...props}>
      {children}
    </button>
  );
}

export function LinkButton({
  href,
  variant = 'secondary',
  children,
}: {
  href: string;
  variant?: keyof typeof BUTTON_VARIANTS;
  children: ReactNode;
}) {
  return (
    <a href={href} className={cx(buttonBase, BUTTON_VARIANTS[variant])}>
      {children}
    </a>
  );
}

export function SegmentedTabs<K extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ key: K; label: string }>;
  value: K;
  onChange: (key: K) => void;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-xl border border-neutral-200 bg-neutral-100/70 p-0.5">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={cx(
            'h-8 rounded-[10px] px-4 text-[13px] font-medium transition-colors',
            value === o.key
              ? 'bg-white text-neutral-950 shadow-[0_1px_2px_rgb(24_35_47/0.08)]'
              : 'text-neutral-500 hover:text-neutral-900'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const TONES = {
  neutral: { pill: 'bg-neutral-100 text-neutral-600', dot: 'bg-neutral-400' },
  blue: { pill: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
  green: { pill: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  amber: { pill: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
} as const;

export type BadgeTone = keyof typeof TONES;

export function Badge({
  tone = 'neutral',
  title,
  children,
}: {
  tone?: BadgeTone;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium whitespace-nowrap',
        TONES[tone].pill
      )}
    >
      <span className={cx('h-1.5 w-1.5 rounded-full', TONES[tone].dot)} aria-hidden />
      {children}
    </span>
  );
}

/** On/off feature marker. Off is quiet text, so the table reads as a map of what is live. */
export function StatusPill({ on, label, title }: { on: boolean; label: string; title?: string }) {
  if (on) {
    return (
      <Badge tone="green" title={title}>
        {label}
      </Badge>
    );
  }
  return (
    <span title={title} className="inline-flex items-center gap-1.5 text-[12px] whitespace-nowrap text-neutral-400">
      <span className="h-1.5 w-1.5 rounded-full border border-neutral-300" aria-hidden />
      {label}
    </span>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx(card, className)}>{children}</div>;
}

export function SectionHeader({ title, subtitle, right }: { title: string; subtitle?: string; right?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 px-6 pt-5 pb-4">
      <div>
        <h2 className="text-[15px] font-medium tracking-[-0.01em] text-neutral-950">{title}</h2>
        {subtitle && <p className={cx('mt-1', mutedText)}>{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export const headCell =
  'sticky top-0 z-10 bg-neutral-50 px-3 py-2.5 text-left text-[12px] font-medium text-neutral-500 ' +
  'border-y border-neutral-200/70 whitespace-nowrap first:pl-6 last:pr-6';
export const cell = 'px-3 py-3.5 text-[13px] text-neutral-900 align-middle whitespace-nowrap first:pl-6 last:pr-6';
export const numCell = cx(cell, 'text-right tabular-nums');
export const row = 'border-b border-neutral-100 last:border-0 hover:bg-neutral-50/70 transition-colors';

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-6 py-5 text-[13px] text-neutral-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-900" />
      {label}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="rounded-xl bg-red-50 px-3.5 py-2.5 text-[13px] text-red-700 ring-1 ring-red-100">
      {children}
    </div>
  );
}
